import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import EmojiPicker, { Theme } from "emoji-picker-react";
import { useTranslation } from "react-i18next";

interface ReactionEntry {
  emoji: string;
  count: number;
  reacted: boolean;
}

const BlogReactions = ({ postId }: { postId: string }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [reactions, setReactions] = useState<ReactionEntry[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchReactions();
  }, [postId, user]);

  // Close picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
      }
    };
    if (showPicker) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showPicker]);

  const fetchReactions = async () => {
    const { data } = await (supabase.from("blog_reactions") as any)
      .select("emoji, user_id")
      .eq("post_id", postId);

    const map: Record<string, { count: number; reacted: boolean }> = {};
    (data || []).forEach((r: any) => {
      if (!map[r.emoji]) map[r.emoji] = { count: 0, reacted: false };
      map[r.emoji].count++;
      if (user && r.user_id === user.id) map[r.emoji].reacted = true;
    });

    // Sort by count descending
    const entries = Object.entries(map)
      .map(([emoji, v]) => ({ emoji, ...v }))
      .sort((a, b) => b.count - a.count);

    setReactions(entries);
  };

  const toggle = async (emoji: string) => {
    if (!user) {
      toast.error(t("blog.signInToReact", "Sign in to react"));
      return;
    }
    if (loading) return;
    setLoading(emoji);

    const existing = reactions.find((r) => r.emoji === emoji);
    if (existing?.reacted) {
      await (supabase.from("blog_reactions") as any)
        .delete()
        .eq("post_id", postId)
        .eq("user_id", user.id)
        .eq("emoji", emoji);
    } else {
      await (supabase.from("blog_reactions") as any)
        .insert({ post_id: postId, user_id: user.id, emoji });
    }
    await fetchReactions();
    setLoading(emoji);
    setLoading(null);
  };

  const handlePickerSelect = (emojiData: any) => {
    setShowPicker(false);
    toggle(emojiData.emoji);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 pt-2 relative">
      {reactions.map((r) => (
        <button
          key={r.emoji}
          onClick={() => toggle(r.emoji)}
          disabled={loading === r.emoji}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
            r.reacted
              ? "bg-primary/15 ring-1 ring-primary/40"
              : "bg-muted hover:bg-muted/80"
          } ${loading === r.emoji ? "opacity-50" : ""}`}
        >
          <span>{r.emoji}</span>
          {r.count > 0 && (
            <span className="text-xs font-medium text-muted-foreground">
              {r.count}
            </span>
          )}
        </button>
      ))}

      {/* Add reaction button */}
      <div className="relative" ref={pickerRef}>
        <button
          onClick={() => {
            if (!user) {
              toast.error(t("blog.signInToReact", "Sign in to react"));
              return;
            }
            setShowPicker(!showPicker);
          }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm bg-muted hover:bg-muted/80 text-muted-foreground transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>

        {showPicker && (
          <div className="absolute bottom-full mb-2 left-0 z-50">
            <EmojiPicker
              onEmojiClick={handlePickerSelect}
              theme={Theme.DARK}
              width={300}
              height={400}
              searchPlaceholder="Search emoji…"
              lazyLoadEmojis
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogReactions;
